'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useAnalytics } from '@/data';
import { useEcomStoreSelector } from '@/data';
import { CartLineItem } from '@/types';

// 10 minutes in milliseconds
const ABANDON_CART_TIMEOUT = 10 * 60 * 1000; // 10 minutes

/**
 * Component that tracks abandon cart events globally
 * Should be placed at the layout level to ensure it's always active
 */
export const AbandonCartTracker = () => {
  const analytics = useAnalytics();
  const { line_items, entity_id } = useEcomStoreSelector([
    'line_items',
    'entity_id',
  ]);

  const abandonCartTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentCartItemsRef = useRef<CartLineItem[]>([]);

  // Clear any existing timer
  const clearAbandonCartTimer = useCallback(() => {
    if (abandonCartTimerRef.current) {
      clearTimeout(abandonCartTimerRef.current);
      abandonCartTimerRef.current = null;
    }
  }, []);

  // Start abandon cart timer
  const startAbandonCartTimer = useCallback(
    (cartItems: CartLineItem[], entityId: string) => {
      currentCartItemsRef.current = cartItems;
      clearAbandonCartTimer();

      // Set timer for 10 minutes
      abandonCartTimerRef.current = setTimeout(() => {
        if (currentCartItemsRef.current.length > 0) {
          analytics.measureAbandonCartGA4({
            cartItems: currentCartItemsRef.current,
            trigger: 'inactivity',
            entityId,
          });
        }
      }, ABANDON_CART_TIMEOUT);
    },
    [analytics, clearAbandonCartTimer],
  );

  // Reset abandon cart timer
  const resetAbandonCartTimer = useCallback(() => {
    if (currentCartItemsRef.current.length > 0) {
      startAbandonCartTimer(currentCartItemsRef.current, entity_id);
    }
  }, [startAbandonCartTimer, entity_id]);

  // Set up abandon cart tracking when cart items change
  useEffect(() => {
    if (line_items && line_items.length > 0) {
      startAbandonCartTimer(line_items, entity_id);
    } else {
      clearAbandonCartTimer();
      currentCartItemsRef.current = [];
    }
  }, [line_items, entity_id, startAbandonCartTimer, clearAbandonCartTimer]);

  // Set up beforeunload tracking for tab close events
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentCartItemsRef.current.length > 0) {
        analytics.measureAbandonCartGA4({
          cartItems: currentCartItemsRef.current,
          trigger: 'tab_close',
          entityId: entity_id,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [analytics]);

  // Set up activity listeners to reset timer on user interaction
  useEffect(() => {
    const resetTimer = () => {
      resetAbandonCartTimer();
    };

    const events = ['click', 'scroll', 'keydown', 'mousemove', 'touchstart'];

    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [resetAbandonCartTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAbandonCartTimer();
    };
  }, [clearAbandonCartTimer]);

  // This component doesn't render anything
  return null;
};
